import React, { useContext } from 'react';
import HeaderNavBarContext from '../context/HeaderNavBarContext';
import UserLinks from './UserLinks';
import Box from '../../../components/Box/Box';
import { StandAloneLink, Link } from '../../../components/Link';
import { Title5 } from '../../../components/Title';
import { THeaderLinks, TNavBarTranslations } from '../../../types/THeaderConfig';
import type { TMenuLink } from '../../../types/TMenuLink';
import { PrimaryButton } from '../../../components/Button';

type TNavBarProps = {
  showHamburgerMenu: () => void;
  menuLinks?: TMenuLink[];
  trackGAEvent?: (eventName: string, eventAction: string) => void;
  logoDesktopSrc: string;
  logoMobileSrc: string;
  translations?: TNavBarTranslations;
  wrapHeadLogoH1Tag?: boolean;
  showSkipToMainLink?: boolean;
  showSkipToFooterLink?: boolean;
};

type TRenderLogo = {
  shouldWrap?: boolean;
  logoMarkup: React.ReactNode;
};

const getLogoAltText = (text?: string) => text ?? window?.location?.hostname ?? 'Homepage logo';
function generateLogoMarkup(
  logoDesktopSrc: string,
  translations: TNavBarTranslations | undefined,
  logoMobileSrc: string,
) {
  return (
    <StandAloneLink
      className="hz-Header-logo"
      href="/"
      data-role="homepage"
      aria-label="Homepage"
      tabIndex={0}
      key="header-logo"
    >
      <img src={logoDesktopSrc} className="hz-Header-logo-desktop" alt={getLogoAltText(translations?.logoAltText)} />
      <img src={logoMobileSrc} className="hz-Header-logo-mobile" alt={getLogoAltText(translations?.logoAltText)} />
    </StandAloneLink>
  );
}

function wrapH1(logoMarkup: React.ReactNode, wrapHeadLogoH1Tag?: boolean) {
  return wrapHeadLogoH1Tag ? <Title5 as="h1">{logoMarkup}</Title5> : [logoMarkup];
}

function RenderLogo({ shouldWrap, logoMarkup }: TRenderLogo) {
  return wrapH1(logoMarkup, shouldWrap);
}

const RenderHeaderLinks: React.FC<{
  headerLinks: THeaderLinks | null | undefined;
  onClickHandler: (index: number) => void;
}> = ({ headerLinks, onClickHandler }) => {
  if (!headerLinks) return null;

  const getKey = (text: string, url: string) => `${text}-${url}`;

  return (
    <Box as="ul" parent="Header" name="links">
      {headerLinks.map(({ text, url }, index) => (
        <li key={getKey(text, url)}>
          <Link href={url} onClick={() => onClickHandler(index)} tabIndex={0}>
            {text}
          </Link>
        </li>
      ))}
    </Box>
  );
};

const NavBar = ({
  showHamburgerMenu,
  menuLinks,
  trackGAEvent,
  logoMobileSrc,
  logoDesktopSrc,
  translations,
  wrapHeadLogoH1Tag,
  showSkipToMainLink,
  showSkipToFooterLink,
}: TNavBarProps) => {
  const { headerLinks, userLinks } = useContext(HeaderNavBarContext) ?? {};

  const onClickHandler = (index: number) => {
    if (trackGAEvent) {
      trackGAEvent('header-link-clicked', index.toString());
    }
  };

  const HeaderLogoMarkup = generateLogoMarkup(logoDesktopSrc, translations, logoMobileSrc);

  const performSkipToLink = (targetId: string, targetContainer: HTMLElement, offset: number = 0) => {
    const targetContainerTop = targetContainer.getBoundingClientRect().top + window.scrollY;
    const headerTop = document.querySelector('.hz-Header')?.getBoundingClientRect().height || 0;

    window.location.href = targetId;
    window.scrollTo({ top: targetContainerTop - headerTop - offset });
  };

  const handleSkipToMain = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const offset = 20;
    const targetId = e.currentTarget.getAttribute('href');
    if (!targetId) return;

    const mainContainer = document.querySelector(targetId) as HTMLElement;
    if (!mainContainer) return;

    performSkipToLink(targetId, mainContainer, offset);
  };

  const handleSkipToFooter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const targetId = e.currentTarget.getAttribute('href');
    if (!targetId) return;

    const footerContainer = document.querySelector(targetId) as HTMLElement;
    if (!footerContainer) return;

    performSkipToLink(targetId, footerContainer);
  };

  return (
    <Box as="div" parent="Header" name="navBar" className="hz-Header-bigger-touchAreas">
      <Box as="div" parent="Header" name="maxWidth">
        <RenderLogo shouldWrap={wrapHeadLogoH1Tag} logoMarkup={HeaderLogoMarkup} />
        {showSkipToMainLink && (
          <PrimaryButton
            as="a"
            href="#main-container"
            className="hz-Header-skipToMain"
            data-role="skip-to-main"
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => handleSkipToMain(e)}
          >
            {translations?.skipToMainLinkText}
          </PrimaryButton>
        )}
        {showSkipToFooterLink && (
          <PrimaryButton
            as="a"
            href="#footer-container"
            className="hz-Header-skipToFooter"
            data-role="skip-to-footer"
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => handleSkipToFooter(e)}
          >
            {translations?.skipToFooterLinkText}
          </PrimaryButton>
        )}
        <RenderHeaderLinks headerLinks={headerLinks} onClickHandler={onClickHandler} />
        {userLinks && <UserLinks showHamburgerMenu={showHamburgerMenu} menuLinks={menuLinks} />}
      </Box>
    </Box>
  );
};

export default NavBar;
